import React, {useCallback, useReducer, useRef} from 'react';
import {createSelector} from 'reselect';
import {useSelector, useDispatch} from 'react-redux';

import * as S from './LocationMap.styles';

import GoogleMap from './GoogleMap';

import {detailsNavigationIds} from '../../details.config';

import {
  // locationMapImageSelectorFn,
  latLongSelectorFn,
  pointOfInterestsDataSelector,
  detailsStaticDataSelector,
  ratingAndReviewsBreakupSelector
} from '../../selectors';

import {selectedPlacesSelector, showAllPlacesSelector} from '../../../HotelsMap/redux/selectors';
import {GoogleMapLocationType} from '../../../HotelsMap/redux/types';
import {updateSelectedPlaces, showOnlyUserSelectedLocations, showAllNearbyPlaces, updateShowAllPlaces} from '../../../HotelsMap/redux/actions';

import CloseCircleFillIcon from '../../../../../stories/Icons/CloseCircleFillIcon';

const latLongSelector = createSelector([latLongSelectorFn], latLong => latLong);

enum LocationMapStateActionTypes {
  updateUserSearchedLocations = 'updateUserSearchedLocations',
  removeUserSearchedLocations = 'removeUserSearchedLocations',
}

const reducer = (state, action) => {
  switch (action.type) {
    case LocationMapStateActionTypes.updateUserSearchedLocations:
      let localStateCopy = {...state};

      let normalisedIncomingPlaces = action.payload?.userSearchedLocations;

      if (!Array.isArray(action.payload?.userSearchedLocations)) {
        normalisedIncomingPlaces = [action.payload?.userSearchedLocations];
      }

      if (localStateCopy.userSearchedLocations.length > 3) {
        localStateCopy.userSearchedLocations = localStateCopy.userSearchedLocations.slice(1);
      }

      const incomingPlaceAlreadyPresent = normalisedIncomingPlaces.some(place => {
        return localStateCopy.userSearchedLocations.some(prevPlace => prevPlace._id === place._id);
      });

      return {
        ...state,
        userSearchedLocations: [
          ...(!incomingPlaceAlreadyPresent ? localStateCopy.userSearchedLocations : []),
          ...normalisedIncomingPlaces,
        ],
      };

    case LocationMapStateActionTypes.removeUserSearchedLocations:
      return {
        ...state,
        userSearchedLocations: state.userSearchedLocations.filter(
          place => place._id !== action.payload?.placeId,
        ),
      };

    default:
      throw new Error(`${action.type} is not supported in LocationMap state reducer`);
  }
};

const initState = {
  userSearchedLocations: [],
};

const LocationMap = () => {
  const [state, stateDispatch] = useReducer(reducer, initState);

  const updateUserSearchedLocations = useCallback(
    locations => {
      stateDispatch({
        type: LocationMapStateActionTypes.updateUserSearchedLocations,
        payload: {
          userSearchedLocations: locations,
        },
      });
    },
    [stateDispatch],
  );

  const removeUserSearchedLocation = useCallback(
    place => {
      stateDispatch({
        type: LocationMapStateActionTypes.removeUserSearchedLocations,
        payload: {
          placeId: place,
        },
      });
    },
    [stateDispatch],
  );
  
  const {la, lo} = useSelector(latLongSelector);
  const pointOfInterestData = useSelector(pointOfInterestsDataSelector);
  const {detailsHotelName, detailsHotelAddress} = useSelector(detailsStaticDataSelector);
  const {location} = useSelector(ratingAndReviewsBreakupSelector);
  
  const selectedPlaces = useSelector(selectedPlacesSelector);
  const showAllPlaces = useSelector(showAllPlacesSelector);
  
  const pointOfInterestPlacesData = pointOfInterestData.filter(d => d.title === 'poi');
  const transitPointPlacesData = pointOfInterestData.filter(d => d.title === 'transit_point');
  const othersPlacesData = pointOfInterestData.filter(d => d.title === 'others');

  const pointOfInterestsList = pointOfInterestPlacesData?.[0]?.data || [];
  const transitPointPlacesList = transitPointPlacesData?.[0]?.data || [];
  const othersPlaceList = othersPlacesData?.[0]?.data || [];
  const nearbyAttractionsList = pointOfInterestsList.concat(othersPlaceList);

  const identifiableNearbyAttractionList = nearbyAttractionsList.map(item => ({
    ...item,
    type: GoogleMapLocationType.attraction,
  }));

  const identifiableTransitPlacesList = transitPointPlacesList.map(item => ({
    ...item,
    type: GoogleMapLocationType.transit,
  }));

  const allNearbyLocationPlaces = identifiableNearbyAttractionList.concat(
    identifiableTransitPlacesList,
  );

  const dispatch = useDispatch();

  const setSelectedPlaces = places => dispatch(updateSelectedPlaces(places));
  const showHotel = () => dispatch(showOnlyUserSelectedLocations());

  const getPlaceEventPropsForMapInteraction = (place, typeOfPlace) => {
    const updatedPlace = {...place, type: typeOfPlace};

    let eventLabel = '';

    if (typeOfPlace === GoogleMapLocationType.attraction) {
      eventLabel = GoogleMapLocationType.attraction;
    }

    if (typeOfPlace === GoogleMapLocationType.transit) {
      eventLabel = GoogleMapLocationType.transit;
    }

    return {
      onClick() {
        setSelectedPlaces(updatedPlace);
      },
    };
  };

  const locationAutosuggestRef = useRef(null);
  const mapContainerRef = useRef(null);

  const thereAreSelectedPlacesOnMapWhichAreNotUserSelected = !!selectedPlaces.filter(
    place => place.type !== GoogleMapLocationType.userSelected,
  ).length;

  const anyNearbyLocationIsNotSelectedOnMap = !selectedPlaces.some(
    place =>
      place.type === GoogleMapLocationType.attraction ||
      place.type === GoogleMapLocationType.transit,
  );

  const showAllNearbyPlacesShouldBeVisible = anyNearbyLocationIsNotSelectedOnMap;

  const showHotelShouldBeVisible = thereAreSelectedPlacesOnMapWhichAreNotUserSelected;

  const thereArePlacesToShow = !!nearbyAttractionsList.length || !!transitPointPlacesList.length;

  return (
    <S.LocationMapContainer id={detailsNavigationIds.location}>
      <S.LocationMapHeader>
        <S.LocationMapHeaderInnerWrap>
          <S.LocationMapHeaderText>Location</S.LocationMapHeaderText>
        </S.LocationMapHeaderInnerWrap>

        <S.LocationMapHeadSubTextWrap>
            <S.LocationHotelAddressText>{detailsHotelName}: {detailsHotelAddress}</S.LocationHotelAddressText>
            <S.LocationHotelReviewText>Guests rated {location} for the location of this hotel</S.LocationHotelReviewText>
        </S.LocationMapHeadSubTextWrap>

      </S.LocationMapHeader>

      <S.ContentContainer>
        {showAllNearbyPlacesShouldBeVisible && (
          <S.MapActionButtonContainer>
            <S.MapActionButton
              onClick={() => {
                dispatch(showAllNearbyPlaces(allNearbyLocationPlaces));
                // setShowAllPlaces(true);
              }}
            >
              SHOW ALL PLACES
            </S.MapActionButton>
          </S.MapActionButtonContainer>
        )}

        {showHotelShouldBeVisible && (
          <S.MapActionButtonContainer>
            <S.MapActionButton
              onClick={() => {
                showHotel();
              }}
            >
              SHOW HOTEL
            </S.MapActionButton>
          </S.MapActionButtonContainer>
        )}        

        {thereArePlacesToShow && (
          <S.WhatIsAroundContainer>
            <S.SearchNearByLocation>
              <S.InputWrapper>
                <S.LocationSearchInput
                  placeholder="Search for landmark, hotel or area"
                  ref={locationAutosuggestRef}
                />
                <S.SearchIconWrap></S.SearchIconWrap>
              </S.InputWrapper>
            </S.SearchNearByLocation>

            <S.PlacesListContainer>
              <S.ListBoxWrapper>
                {!!nearbyAttractionsList.length && (
                  <S.ListHeading>NEARBY ATTRACTIONS (A)</S.ListHeading>
                )}

                {!!nearbyAttractionsList.length && (
                  <S.List>
                    {nearbyAttractionsList.map(nearbyAttraction => {
                      return (
                        <S.ListItem
                          key={nearbyAttraction.n}
                          {...getPlaceEventPropsForMapInteraction(
                            nearbyAttraction,
                            GoogleMapLocationType.attraction,
                          )}
                        >
                          <S.ListItemWrapper>
                            <S.GreenRound
                              active={
                                !!selectedPlaces.length &&
                                !!selectedPlaces.some(
                                  place => place.type === GoogleMapLocationType.attraction,
                                ) &&
                                selectedPlaces.some(place => place._id === nearbyAttraction._id)
                              }
                            />
                            <S.ListItemTextsContainer>
                              <S.ListItemText>{nearbyAttraction.n}</S.ListItemText>

                              {nearbyAttraction.dist_display && nearbyAttraction.unit_display && (
                                <S.DistanceText>
                                  {nearbyAttraction.dist_display} {nearbyAttraction.unit_display}
                                </S.DistanceText>
                              )}
                            </S.ListItemTextsContainer>
                          </S.ListItemWrapper>
                        </S.ListItem>
                      );
                    })}
                  </S.List>
                )}

                {!!transitPointPlacesList.length && (
                  <S.ListHeading>NEARBY TRANSIT (T)</S.ListHeading>
                )}

                {!!transitPointPlacesList.length && (
                  <S.List>
                    {transitPointPlacesList.map(transitDataForPlace => {
                      return (
                        <S.ListItem
                          key={transitDataForPlace.n}
                          {...getPlaceEventPropsForMapInteraction(
                            transitDataForPlace,
                            GoogleMapLocationType.transit,
                          )}
                        >
                          <S.ListItemWrapper>
                            <S.GreenRound
                              active={
                                !!selectedPlaces.length &&
                                !!selectedPlaces.some(
                                  place => place.type === GoogleMapLocationType.transit,
                                ) &&
                                selectedPlaces.some(place => place._id === transitDataForPlace._id)
                              }
                            />
                            <S.ListItemTextsContainer>
                              <S.ListItemText>{transitDataForPlace.n}</S.ListItemText>
                              {transitDataForPlace.dist_display &&
                                transitDataForPlace.unit_display && (
                                  <S.DistanceText>
                                    {transitDataForPlace.dist_display}{' '}
                                    {transitDataForPlace.unit_display}
                                  </S.DistanceText>
                                )}
                            </S.ListItemTextsContainer>
                          </S.ListItemWrapper>
                        </S.ListItem>
                      );
                    })}
                  </S.List>
                )}
              </S.ListBoxWrapper>
            </S.PlacesListContainer>
          </S.WhatIsAroundContainer>
        )}

        <S.MapContainer flex={thereArePlacesToShow ? 0.74 : 1}>
          <GoogleMap
            lat={la}
            lng={lo}
            allNearbyLocationPlaces={allNearbyLocationPlaces}
            selectedPlaces={selectedPlaces}
            showNearbyPlaces={!showAllNearbyPlacesShouldBeVisible}
            refs={{
              mapContainerRef,
              locationAutosuggestRef,
            }}
            updateUserSearchedLocations={updateUserSearchedLocations}
            userSearchedLocations={state.userSearchedLocations}
          />
        </S.MapContainer>
      </S.ContentContainer>

      <S.AutoSuggestOuterWrapper>
        {(state.userSearchedLocations.map(location => {
          return (
            <S.AutoSuggestContainer>
              <S.LocationMapOuterWrap>
                <S.LocationMapWrapper>
                  {/* <MapLocationIcon /> */}
                  {(location.n && (
                    <S.LocationName>
                      {location.n}
                      <S.LocationNameToolTip>{location.n}</S.LocationNameToolTip>
                    </S.LocationName>
                  )) || null}
                </S.LocationMapWrapper>
                <CloseCircleFillIcon
                  onClick={() => {
                    removeUserSearchedLocation(location._id);
                  }}
                />
              </S.LocationMapOuterWrap>
              <S.TimeDistanceDurationWrapper>
                {(location.timeFromHotel && (
                  <S.TimeDuration>{location.timeFromHotel} | </S.TimeDuration>
                )) || null}
                {(location.distanceFromHotel && (
                  <S.DistanceDuration>{location.distanceFromHotel}</S.DistanceDuration>
                ) || null)}
              </S.TimeDistanceDurationWrapper>
            </S.AutoSuggestContainer>
          );
        })) || null}
      </S.AutoSuggestOuterWrapper>
    </S.LocationMapContainer>
  );
};

export default LocationMap;
